import { gql, useLazyQuery } from '@apollo/client';
import { useCallback, useState } from 'react';
import { StyledModal } from '~/components/dialogs/dialog.style';
import { Link, LinkProps } from '~/components/link';
import { ScrollablePdf } from '~/components/terms/scrollable-terms/scrollable-pdf.component';
import { useToggle } from '~/hooks';
import { ContractVersion } from '~/typings/context';
import {
  Flex_FetchPersonalizedContractQuery,
  Flex_FetchPersonalizedContractQueryVariables,
} from '~/typings/graphql';
import { TERMS_URL } from '~/utils/constants';
import { determineLanguage } from '~/utils/funnels';

export const FETCH_PERSONALIZED_CONTRACT = gql`
  query Flex_FetchPersonalizedContract($contractVersionId: Int!, $utilityAccountId: Int!) {
    contractVersion(id: $contractVersionId) {
      id
      personalizedFile(utilityAccountId: $utilityAccountId)
    }
  }
`;

export type ContractLinkProps = {
  contract: ContractVersion;
  linkProps?: LinkProps;
  showPersonalizedContractPreview: boolean;
  utilityAccountId?: number;
};

export const ContractLink = ({
  contract,
  linkProps,
  showPersonalizedContractPreview,
  utilityAccountId,
}: ContractLinkProps) => {
  const [isOpen, toggle] = useToggle(false);
  const [personalizedFile, setPersonalizedFile] = useState<string | null>(null);

  const openContractUrl = useCallback(() => {
    const language = determineLanguage() || 'en';
    const href = `${TERMS_URL}?id=${contract.id}&lang=${language}`;
    window.open(href, '_blank');
  }, [contract.id]);

  const handleOnCompleted = useCallback(
    data => {
      if (data?.contractVersion?.personalizedFile) {
        setPersonalizedFile(data.contractVersion.personalizedFile);
        toggle(true);
      } else {
        openContractUrl();
      }
    },
    [openContractUrl, toggle],
  );

  const [getPersonalizedFile] = useLazyQuery<
    Flex_FetchPersonalizedContractQuery,
    Flex_FetchPersonalizedContractQueryVariables
  >(FETCH_PERSONALIZED_CONTRACT, { onCompleted: handleOnCompleted, onError: openContractUrl });

  const handleOnClick = useCallback(() => {
    const language = determineLanguage() || 'en';

    if (showPersonalizedContractPreview && utilityAccountId && language === 'en') {
      getPersonalizedFile({
        variables: { contractVersionId: contract.id, utilityAccountId },
      });
    } else {
      openContractUrl();
    }
  }, [
    contract.id,
    getPersonalizedFile,
    openContractUrl,
    showPersonalizedContractPreview,
    utilityAccountId,
  ]);

  return (
    <>
      <StyledModal
        isOpen={isOpen}
        onDismiss={() => toggle(false)}
        size="large"
        aria-label="personalized-contract-preview"
        data-testid="personalized-contract-preview"
      >
        <StyledModal.HeaderCentered title={contract.displayName} onClose={() => toggle(false)} />
        <ScrollablePdf pdfUrl={personalizedFile || ''} />
      </StyledModal>
      <Link
        color="primary"
        id={`ContractVersion-${contract.id}`}
        openInNewTab={false}
        onClick={() => handleOnClick()}
        {...linkProps}
      >
        {contract.displayName}
      </Link>
    </>
  );
};
